import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Boxes, Box, AlertTriangle } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useStackRecoveryStats, useRetrieveObject, useConversionOptions } from '../hooks'
import { ScanFirstOption } from './ScanFirstOption'
import { ConversionFormatPicker, buildConvertFormats, type ConversionMode } from './ConversionFormatPicker'
import { RedObjectsListDialog } from './RedObjectsListDialog'

interface RetrieveObjectDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
    scenario?: number | null
}

/**
 * Retrieve a whole object (and its dependency chain) from the vault. Mirrors
 * RequestRecoveryDialog but without the source-layer slider, and runs
 * CreateRecoverRequests with VRecover = 'Object'.
 */
export function RetrieveObjectDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
}: RetrieveObjectDialogProps) {
    const { t } = useTranslation()
    const { data: stats, isLoading: isLoadingStats } = useStackRecoveryStats(objectID)
    const retrieveMutation = useRetrieveObject()
    const [scanFirst, setScanFirst] = useState(false)
    const [recoverRedObjects, setRecoverRedObjects] = useState<'Yes' | 'No'>('No')
    const [showRedObjectsDialog, setShowRedObjectsDialog] = useState(false)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [showErrorDialog, setShowErrorDialog] = useState(false)
    const [recoveryErrorMessage, setRecoveryErrorMessage] = useState<string | null>(null)
    // Egress conversion. One object here, so 'all' and 'per object' land on the same result — the
    // picker still offers both because the wording differs and the customer should recognise the
    // same control they saw on the stack request.
    const [conversionMode, setConversionMode] = useState<ConversionMode>('none')
    const [conversionAllTargets, setConversionAllTargets] = useState<string[]>([])
    const [conversionPerObject, setConversionPerObject] = useState<Record<string, string[]>>({})
    const { data: conversionData, isLoading: isLoadingConversion } = useConversionOptions(
        objectID,
        { mode: 'object', scenario: scenario ?? 1 },
        open,
    )

    const handleConfirm = async () => {
        if (!objectID) return
        setRecoveryErrorMessage(null)

        try {
            const result = await retrieveMutation.mutateAsync({
                objectID,
                recoverRedObjects,
                scanFirst,
                scenario: scenario ?? undefined,
                convertFormats: buildConvertFormats(
                    conversionMode,
                    conversionData?.objects ?? [],
                    conversionAllTargets,
                    conversionPerObject,
                ),
            })

            if (result?.success) {
                setShowSuccessDialog(true)
            } else {
                const msg =
                    (result as { sqlMessage?: string; outcome?: string })?.sqlMessage ||
                    (result as { sqlMessage?: string; outcome?: string })?.outcome ||
                    t('objects.recoveryRequestErrorMessage')
                setRecoveryErrorMessage(msg)
                setShowErrorDialog(true)
            }
        } catch (error: unknown) {
            const msg =
                (error as { response?: { data?: { data?: { sqlMessage?: string } } } })?.response?.data?.data?.sqlMessage ||
                (error as Error)?.message ||
                t('objects.recoveryRequestErrorMessage')
            setRecoveryErrorMessage(msg)
            setShowErrorDialog(true)
        }
    }

    const handleSuccessClose = () => {
        setShowSuccessDialog(false)
        onOpenChange(false)
    }

    if (!objectID) {
        return null
    }

    return (
        <>
            <Dialog open={open && !showSuccessDialog && !showErrorDialog} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[700px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.retrieveObject', 'Retrieve Object')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.retrieveObjectDescription', { objectID: objectID || '', defaultValue: `Retrieve ${objectID} and its dependency chain from the vault.` })}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Stats Cards - matching RequestRecoveryDialog style */}
                        <div className="flex justify-center gap-4">
                            {/* Services Card */}
                            <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                                <div className="flex items-center space-x-2">
                                    <Boxes className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm font-medium">{t('objects.numberOfServices')}</p>
                                        <p className="text-2xl font-bold">
                                            {isLoadingStats ? '-' : (stats?.servicesCount || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Objects Card */}
                            <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                                <div className="flex items-center space-x-2">
                                    <Box className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm font-medium">{t('objects.numberOfObjects')}</p>
                                        <p className="text-2xl font-bold">
                                            {isLoadingStats ? '-' : (stats?.objectsCount || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Red Objects Card */}
                            <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                                <div className="flex items-center space-x-2">
                                    <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">{t('objects.redObjects')}</p>
                                        <p className="text-2xl font-bold text-red-600">
                                            {isLoadingStats ? '-' : (stats?.redObjectsCount || 0)}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute bottom-4 right-4"
                                    onClick={() => setShowRedObjectsDialog(true)}
                                    disabled={!stats?.redObjectsCount || stats.redObjectsCount === 0}
                                >
                                    {t('objects.listRedObjects')}
                                </Button>
                            </div>
                        </div>

                        {/* Recover Red or Green Objects */}
                        <div className="space-y-3">
                            <Label>{t('objects.recoverObjects')}</Label>
                            <div className="flex justify-center gap-3">
                                <Button
                                    type="button"
                                    variant={recoverRedObjects === 'Yes' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setRecoverRedObjects('Yes')}
                                    className={recoverRedObjects === 'Yes' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                                >
                                    {t('objects.retrieveRedObjects')}
                                </Button>
                                <Button
                                    type="button"
                                    variant={recoverRedObjects === 'No' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setRecoverRedObjects('No')}
                                    className={recoverRedObjects === 'No' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                                >
                                    {t('objects.retrieveGreenObjects')}
                                </Button>
                            </div>
                        </div>

                        {/* Convert disk images on egress. Offered only when the object actually
                            carries a readable disk image — see ConversionFormatPicker. */}
                        <ConversionFormatPicker
                            options={conversionData?.objects ?? []}
                            isLoading={isLoadingConversion}
                            mode={conversionMode}
                            onModeChange={setConversionMode}
                            allTargets={conversionAllTargets}
                            onAllTargetsChange={setConversionAllTargets}
                            perObject={conversionPerObject}
                            onPerObjectChange={setConversionPerObject}
                        />
                        <ScanFirstOption checked={scanFirst} onChange={setScanFirst} disabled={retrieveMutation.isPending} />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={retrieveMutation.isPending}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleConfirm} disabled={retrieveMutation.isPending}>
                            {retrieveMutation.isPending
                                ? t('common.processing')
                                : t('objects.confirmRetrieveObject', 'Confirm Retrieve')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Red Objects List Dialog */}
            <RedObjectsListDialog
                open={showRedObjectsDialog}
                onOpenChange={setShowRedObjectsDialog}
                objectID={objectID}
            />

            {/* Success Dialog */}
            <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.recoveryRequested')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.recoveryRequestedMessage', { objectID })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('objects.recoveryNeedsApproval')}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleSuccessClose}>{t('common.yes')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Error Dialog */}
            <Dialog open={showErrorDialog} onOpenChange={(o) => { setShowErrorDialog(o); if (!o) setRecoveryErrorMessage(null) }}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.recoveryRequestError')}</DialogTitle>
                        <DialogDescription>
                            {recoveryErrorMessage || t('objects.recoveryRequestErrorMessage')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => { setShowErrorDialog(false); setRecoveryErrorMessage(null) }}>
                            {t('common.close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
